import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import Button from '@mui/material/Button'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import EditIcon from '@mui/icons-material/Edit'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormControl from '@mui/material/FormControl'
import FormLabel from '@mui/material/FormLabel'
import LaunchIcon from '@mui/icons-material/Launch'

import { BootstrapDialog, BootstrapDialogTitle } from '../shared/BootstrapDialog'
import { validateField, validateOptionalField, descriptionLimit, validateRequiredResourceTags, collectTagData } from '../../utils/formValidation'
import ResourceTagsSection from '../shared/ResourceTagsSection'
import ResourceCheckboxes from '../shared/ResourceCheckboxes'
import TCAIREMFieldsSection from '../shared/TCAIREMFieldsSection'
import CoreResourceFields from '../shared/CoreResourceFields'
import ResourceDescriptionField from '../shared/ResourceDescriptionField'
import { AdminBookModalStyle } from '../styles'
import { setErrorNotification } from '../../reducers/notificationReducer'
import { deleteBook, updateBook } from '../../reducers/booksReducer'
import { openExternal } from '../../utils/safeUrl'

const AdminBookModal = (props) => {
    const user = useSelector(state => state.user)
    const dispatch = useDispatch()

    // form state (no need to use redux for form state)
    const [name, setName] = useState('')
    const [nameError, setNameError] = useState(false)
    const [nameHelper, setNameHelper] = useState('')
    const [authors, setAuthors] = useState('')
    const [authorsError, setAuthorsError] = useState(false)
    const [authorsHelper, setAuthorsHelper] = useState('')
    const [link, setLink] = useState('')
    const [linkError, setLinkError] = useState(false)
    const [linkHelper, setLinkHelper] = useState('')
    const [date, setDate] = useState('')
    const [dateError, setDateError] = useState(false)
    const [dateHelper, setDateHelper] = useState('')
    const [description, setDescription] = useState('')
    const [descriptionError, setDescriptionError] = useState(false)
    const [descriptionHelper, setDescriptionHelper] = useState('')
    const [contentTags, setContentTags] = useState([])
    const [medicalTags, setMedicalTags] = useState([])
    const [adminCategoryTags, setAdminCategoryTags] = useState([])
    const [experienceTags, setExperienceTags] = useState([])
    const [demographicTags, setDemographicTags] = useState([])
    const [showTagErrors, setShowTagErrors] = useState(false)
    const [bookType, setBookType] = useState('TB')
    const [isbnBook, setIsbnBook] = useState('')
    const [isbnBookError, setIsbnBookError] = useState(false)
    const [isbnBookHelper, setIsbnBookHelper] = useState('')
    const [isSeminalWork, setIsSeminalWork] = useState(false)
    const [inQuiz, setInQuiz] = useState(false)
    const [isTcairem, setIsTcairem] = useState(false)
    const [isPaid, setIsPaid] = useState(false)
    const [tcairemTags, setTcairemTags] = useState([])
    const [personaTags, setPersonaTags] = useState([])

    // init fields
    useEffect(() => {
        setName(props.title)
        setAuthors(props.authors)
        setLink(props.link)
        setDate(props.pub_date)
        setBookType(props.book_type)
        setIsbnBook(props.isbn)
        setDescription(props.description || '')
        setIsSeminalWork(props.is_seminal_work || false)
        setInQuiz(props.in_quiz || false)
        setContentTags((props.content_tags || []).map(String))
        setMedicalTags((props.medical_specialty_tags || []).map(String))
        setAdminCategoryTags((props.admin_category_tags || []).map(String))
        setExperienceTags((props.experience_tags || []).map(String))
        setDemographicTags((props.demographic_tags || []).map(String))
        setIsTcairem(props.is_tcairem || false)
        setIsPaid(props.is_paid || false)
        setTcairemTags((props.tcairem_type_tags || []).map(String))
        setPersonaTags((props.tcairem_persona_tags || []).map(String))
    }, [props.title, props.authors, props.link, props.pub_date, props.book_type, props.isbn, props.description, props.content_tags, props.medical_specialty_tags, props.admin_category_tags, props.experience_tags, props.demographic_tags, props.is_seminal_work, props.in_quiz, props.is_tcairem, props.is_paid, props.tcairem_type_tags, props.tcairem_persona_tags])

    // reset fields to original values if user wants to
    const reset = () => {
        setName(props.title)
        setAuthors(props.authors)
        setLink(props.link)
        setDate(props.pub_date)
        setIsbnBook(props.isbn)
        setBookType(props.book_type)
        setDescription(props.description || '')
        setIsSeminalWork(props.is_seminal_work || false)
        setInQuiz(props.in_quiz || false)
        setContentTags((props.content_tags || []).map(String))
        setMedicalTags((props.medical_specialty_tags || []).map(String))
        setAdminCategoryTags((props.admin_category_tags || []).map(String))
        setExperienceTags((props.experience_tags || []).map(String))
        setDemographicTags((props.demographic_tags || []).map(String))
        setIsTcairem(props.is_tcairem || false)
        setIsPaid(props.is_paid || false)
        setTcairemTags((props.tcairem_type_tags || []).map(String))
        setPersonaTags((props.tcairem_persona_tags || []).map(String))
        setNameError(false)
        setNameHelper('')
        setAuthorsError(false)
        setAuthorsHelper('')
        setLinkError(false)
        setLinkHelper('')
        setDateError(false)
        setDateHelper('')
        setIsbnBookError(false)
        setIsbnBookHelper('')
        setDescriptionError(false)
        setDescriptionHelper('')
        setShowTagErrors(false)
    }

    // delete resource
    const handleReject = async () => {
        if (window.confirm('Are you sure? Note this resource cannot be retrieved after rejection.')) {
            handleClose()
            await dispatch(deleteBook(props.id, user.staff))
            props.onChanged?.()
        }
    }

    // if user clicks approve/disapprove all I need to do is toggle approved state and update fields
    const handleSubmit = async () => {
        setShowTagErrors(true)
        if (
            validateField(name, setNameError, setNameHelper) &&
            validateField(authors, setAuthorsError, setAuthorsHelper) &&
            validateField(link, setLinkError, setLinkHelper) &&
            validateField(date, setDateError, setDateHelper) &&
            validateOptionalField(description, setDescriptionError, setDescriptionHelper, descriptionLimit(props.description), 'Description') &&
            validateRequiredResourceTags({
                contentTags,
                adminCategoryTags,
                experienceTags,
                demographicTags,
                tcairemTags,
                personaTags,
                isTcairem,
            })
        ) {
            if (window.confirm('Are you sure? Note any changes you have made will be included.')) {
                const data = {
                    ...props,
                    title: name,
                    description: description,
                    authors: authors,
                    link: link,
                    pub_date: date,
                    isbn: isbnBook,
                    book_type: bookType,
                    approved: !props.approved,
                    is_seminal_work: isSeminalWork,
                    in_quiz: inQuiz,
                    is_tcairem: isTcairem,
                    is_paid: isPaid,
                    ...collectTagData({ contentTags, medicalTags, adminCategoryTags, experienceTags, demographicTags, tcairemTags, personaTags, isTcairem }),
                }


                handleClose()
                await dispatch(updateBook(props.id, data, user.staff))
                props.onChanged?.()
            }
        } else {
            dispatch(setErrorNotification('Please fill all required fields before submission'))
        }
    }

    // do not change approved state just update fields
    const handleSave = async () => {
        setShowTagErrors(true)
        if (
            validateField(name, setNameError, setNameHelper) &&
            validateField(authors, setAuthorsError, setAuthorsHelper) &&
            validateField(link, setLinkError, setLinkHelper) &&
            validateField(date, setDateError, setDateHelper) &&
            validateField(isbnBook, setIsbnBookError, setIsbnBookHelper) &&
            validateOptionalField(description, setDescriptionError, setDescriptionHelper, descriptionLimit(props.description), 'Description') &&
            validateRequiredResourceTags({
                contentTags,
                adminCategoryTags,
                experienceTags,
                demographicTags,
                tcairemTags,
                personaTags,
                isTcairem,
            })
        ) {
            const data = {
                title: name,
                description: description,
                authors: authors,
                link: link,
                pub_date: date,
                isbn: isbnBook,
                book_type: bookType,
                is_seminal_work: isSeminalWork,
                in_quiz: inQuiz,
                is_tcairem: isTcairem,
                is_paid: isPaid,
                ...collectTagData({ contentTags, medicalTags, adminCategoryTags, experienceTags, demographicTags, tcairemTags, personaTags, isTcairem }),
            }

            const ok = await dispatch(updateBook(props.id, data, user.staff))
            if (ok) {
                handleClose()
                props.onChanged?.()
            }
        } else {
            dispatch(setErrorNotification('Please fill all required fields before submission'))
        }
    }

    const [open, setOpen] = useState(false)

    const handleClickOpen = () => {
        setShowTagErrors(false)
        setOpen(true)
    }

    const handleClose = () => {
        setShowTagErrors(false)
        setOpen(false)
    }

    const openResource = () => {
        openExternal(props.link)
        return false;
    }

    return (
        <div>
            <Button {...AdminBookModalStyle.viewButton.props} onClick={()=>handleClickOpen()} sx={AdminBookModalStyle.viewButton.style}>
                <EditIcon /> &nbsp; Edit
            </Button>
            <BootstrapDialog
                onClose={()=>handleClose()}
                aria-labelledby={`book-modal-${props.id}`}
                open={open}
                fullWidth
                {...AdminBookModalStyle.modal}
            >
                <BootstrapDialogTitle id={`book-modal-${props.id}`} onClose={()=>handleClose()}>
                    <Box sx={AdminBookModalStyle.titleContainer}>
                        <MenuBookIcon sx={AdminBookModalStyle.icon} />
                        {props.title}
                    </Box>
                </BootstrapDialogTitle>
                    <DialogContent dividers sx={AdminBookModalStyle.content}>
                        <form onSubmit={handleSubmit} id={`book-form-${props.id}`}>
                            <Typography {...AdminBookModalStyle.subheading} gutterBottom>
                                Manage Resource
                            </Typography>
                            {props.proposer && <Typography {...AdminBookModalStyle.proposerText}>
                                Created By: {props.proposer.name} ({props.proposer.email})
                            </Typography>}
                            <Grid container spacing={2} sx={AdminBookModalStyle.formGrid}>
                                <Grid item xs={12}>
                                    <FormControl>
                                        <FormLabel id="book-type">Book Type</FormLabel>
                                            <RadioGroup row aria-labelledby="book-type" name="book-types" defaultValue={bookType}>
                                                <FormControlLabel value="TB" control={<Radio onClick={(e) => setBookType(e.target.value)} />} label="Textbook" />
                                                <FormControlLabel value="NF" control={<Radio onClick={(e) => setBookType(e.target.value)} />} label="Nonfiction" />
                                            </RadioGroup>
                                    </FormControl>
                                </Grid>
                                <CoreResourceFields
                                    name={name} setName={setName} nameError={nameError} setNameError={setNameError} nameHelper={nameHelper} setNameHelper={setNameHelper}
                                    authors={authors} setAuthors={setAuthors} authorsError={authorsError} setAuthorsError={setAuthorsError} authorsHelper={authorsHelper} setAuthorsHelper={setAuthorsHelper}
                                    link={link} setLink={setLink} linkError={linkError} setLinkError={setLinkError} linkHelper={linkHelper} setLinkHelper={setLinkHelper}
                                    date={date} setDate={setDate} dateError={dateError} setDateError={setDateError} dateHelper={dateHelper} setDateHelper={setDateHelper}
                                    validate={validateField}
                                />
                                <ResourceDescriptionField
                                    value={description} setValue={setDescription}
                                    error={descriptionError} setError={setDescriptionError}
                                    helper={descriptionHelper} setHelper={setDescriptionHelper}
                                    limit={descriptionLimit(props.description)}
                                />
                                <ResourceTagsSection
                                    contentTags={contentTags} setContentTags={setContentTags}
                                    medicalTags={medicalTags} setMedicalTags={setMedicalTags}
                                    adminCategoryTags={adminCategoryTags} setAdminCategoryTags={setAdminCategoryTags}
                                    experienceTags={experienceTags} setExperienceTags={setExperienceTags}
                            demographicTags={demographicTags} setDemographicTags={setDemographicTags}
                                    required={true}
                                    forceShowRequiredErrors={showTagErrors}
                                />
                                <ResourceCheckboxes inQuiz={inQuiz} setInQuiz={setInQuiz} isSeminalWork={isSeminalWork} setIsSeminalWork={setIsSeminalWork} />
                                <TCAIREMFieldsSection
                                    isTcairem={isTcairem} setIsTcairem={setIsTcairem}
                                    isPaid={isPaid} setIsPaid={setIsPaid}
                                    tcairemTags={tcairemTags} setTcairemTags={setTcairemTags}
                                    personaTags={personaTags} setPersonaTags={setPersonaTags}
                                    forceShowRequiredErrors={showTagErrors}
                                />
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        value={isbnBook}
                                        onChange={e => setIsbnBook(e.target.value)}
                                        label="Book ISBN"
                                        required
                                        error={isbnBookError}
                                        helperText={isbnBookHelper}
                                        onBlur={()=>validateField(isbnBook, setIsbnBookError, setIsbnBookHelper)}
                                    />
                                </Grid>
                            </Grid>
                            <Button {...AdminBookModalStyle.resetButton} type='button' onClick={() => reset()} sx={{marginTop: '20px'}}>
                                Reset Fields
                            </Button>
                        </form>
                    </DialogContent>
                    <DialogActions>
                        <Button type='button' onClick={()=>openResource()}>
                            View Resource&nbsp;<LaunchIcon {...AdminBookModalStyle.launch}/>
                        </Button>
                        <Button type='button' onClick={()=>handleSave()}>
                            Save Changes
                        </Button>
                        <Button type='submit' form={`book-form-${props.id}`}>
                            {props.approved? 'Disapprove':'Approve'}
                        </Button>
                        <Button type='button' onClick={()=>handleReject()}>
                            {props.approved? 'Delete':'Reject'}
                        </Button>
                    </DialogActions>
            </BootstrapDialog>
        </div>
    )
}

export default AdminBookModal
